import { MRT_ColumnDef } from "material-react-table";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Control, Path, UseFieldArrayPrepend, UseFieldArrayRemove } from "react-hook-form";

import { TOption } from "@/types/common";
import { PRICE_DIMENSION, TPriceDimension } from "@/features/organization/product/constants/productEnums";
import { DefaultLabelCell, DeleteCell, MoneyCell, NumberCell, SelectCell, TextCell } from "@/features/organization/product/components/product-form/formTableCells";
import { TProductForm, TTypePriceForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { defaultTypePriceForm } from "@/features/organization/product/utils/productForm";
import {
  DEFAULT_OPTION_LABEL,
  dimensionHasDefaultRow,
  getTypeDimensions,
  usedValuesExcept,
} from "@/features/organization/product/utils/productUtils";

export type TProductTypePriceRow = TTypePriceForm & { _idx: number };

type TUseProductTypePriceSectionParams = {
  control: Control<TProductForm>;
  productName: string;
  isTierDiscountTarget: boolean;
  fields: TProductTypePriceRow[];
  watchedTypePrices: TTypePriceForm[];
  prepend: UseFieldArrayPrepend<TProductForm, "typePrices">;
  remove: UseFieldArrayRemove;
  rangeCountCell: MRT_ColumnDef<TProductTypePriceRow>["Cell"];
  /** 国籍 / 分野 master options (from useProductMasters). */
  nationalityOptions: TOption[];
  industryOptions: TOption[];
};

type TBuildColumnsParams = {
  selectedDimension: TPriceDimension;
  selectedDimensionValues: (string | null)[];
  control: Control<TProductForm>;
  remove: UseFieldArrayRemove;
  rangeCountCell: MRT_ColumnDef<TProductTypePriceRow>["Cell"];
  nationalityOptions: TOption[];
  industryOptions: TOption[];
};

const getFieldPath = (rowIndex: number, fieldKey: keyof TTypePriceForm): Path<TProductForm> =>
  `typePrices.${rowIndex}.${fieldKey}` as Path<TProductForm>;

const createPriceColumn = (header: string, control: Control<TProductForm>): MRT_ColumnDef<TProductTypePriceRow> => ({
  id: "unitPrice",
  header,
  size: 220,
  Cell: ({ row }) => <MoneyCell control={control} name={getFieldPath(row.original._idx, "unitPrice")} />,
});

const createValueColumn = (
  header: string,
  cell: MRT_ColumnDef<TProductTypePriceRow>["Cell"],
): MRT_ColumnDef<TProductTypePriceRow> => ({ id: "value", header, size: 520, Cell: cell });

const createActionColumn = (remove: UseFieldArrayRemove): MRT_ColumnDef<TProductTypePriceRow> => ({
  id: "action",
  header: "",
  size: 52,
  Cell: ({ row }) => (row.original.isDefault ? null : <DeleteCell onClick={() => remove(row.original._idx)} />),
});

const buildColumnsByDimension = ({
  selectedDimension,
  selectedDimensionValues,
  control,
  remove,
  rangeCountCell,
  nationalityOptions,
  industryOptions,
}: TBuildColumnsParams): MRT_ColumnDef<TProductTypePriceRow>[] => {
  const defaultCell = () => <DefaultLabelCell label={DEFAULT_OPTION_LABEL[selectedDimension]} />;

  let baseColumns: MRT_ColumnDef<TProductTypePriceRow>[];

  switch (selectedDimension) {
    case PRICE_DIMENSION.NATIONALITY:
      baseColumns = [
        createValueColumn("国籍", ({ row }) =>
          row.original.isDefault ? defaultCell() : (
            <SelectCell
              control={control}
              name={getFieldPath(row.original._idx, "nationalityId")}
              options={nationalityOptions}
              clearable
              disabledValues={usedValuesExcept(selectedDimensionValues, row.index)}
            />
          ),
        ),
        createPriceColumn("単価/名", control),
      ];
      break;
    case PRICE_DIMENSION.FIELD:
      baseColumns = [
        createValueColumn("分野", ({ row }) =>
          row.original.isDefault ? defaultCell() : (
            <SelectCell
              control={control}
              name={getFieldPath(row.original._idx, "fieldId")}
              options={industryOptions}
              disabledValues={usedValuesExcept(selectedDimensionValues, row.index)}
            />
          ),
        ),
        createPriceColumn("単価/件", control),
      ];
      break;
    case PRICE_DIMENSION.TIER:
      baseColumns = [
        {
          id: "value",
          header: "ティア",
          size: 220,
          Cell: ({ row }) =>
            row.original.isDefault ? defaultCell() : (
              <TextCell control={control} name={getFieldPath(row.original._idx, "variantValue")} />
            ),
        },
        {
          id: "lowerLimit",
          header: "下限",
          size: 180,
          Cell: ({ row }) => (
            <NumberCell
              control={control}
              name={getFieldPath(row.original._idx, "lowerLimit")}
              suffix="名"
            />
          ),
        },
        {
          id: "upperLimit",
          header: "上限",
          size: 180,
          Cell: ({ row }) => (
            <NumberCell
              control={control}
              name={getFieldPath(row.original._idx, "upperLimit")}
              suffix="名"
            />
          ),
        },
        {
          id: "tier.unitPrice",
          header: "単価",
          size: 160,
          Cell: ({ row }) => <MoneyCell control={control} name={getFieldPath(row.original._idx, "unitPrice")} />,
        },
        {
          id: "range",
          header: "範囲人数",
          size: 110,
          muiTableBodyCellProps: { align: "right" },
          Cell: rangeCountCell,
        },
      ];
      break;
    default:
      // その他 — no default row (C3); every row is a free-text 項目.
      baseColumns = [
        createValueColumn("項目名", ({ row }) => (
          <TextCell control={control} name={getFieldPath(row.original._idx, "variantValue")} />
        )),
        createPriceColumn("単価/件", control),
      ];
  }

  return [...baseColumns, createActionColumn(remove)];
};

const EMPTY_DIMENSION_VALUES: (string | null)[] = [];

type TDimensionRowStats = {
  hasDefaultRow: boolean;
  freeRowCount: number;
};

const useDimensionRowStats = (dimensionRows: TProductTypePriceRow[]): TDimensionRowStats =>
  useMemo(
    () =>
      dimensionRows.reduce<TDimensionRowStats>(
        (acc, row) => {
          if (row.isDefault) {
            acc.hasDefaultRow = true;
          } else {
            acc.freeRowCount += 1;
          }
          return acc;
        },
        { hasDefaultRow: false, freeRowCount: 0 },
      ),
    [dimensionRows],
  );

const useSelectedDimensionValues = (
  selectedDimension: TPriceDimension,
  dimensionRows: TProductTypePriceRow[],
  watchedTypePrices: TTypePriceForm[],
): (string | null)[] => {
  const isSelectDimension =
    selectedDimension === PRICE_DIMENSION.FIELD || selectedDimension === PRICE_DIMENSION.NATIONALITY;

  const dimensionValueField: "fieldId" | "nationalityId" =
    selectedDimension === PRICE_DIMENSION.FIELD ? "fieldId" : "nationalityId";

  return useMemo(() => {
    if (!isSelectDimension) return EMPTY_DIMENSION_VALUES;
    return dimensionRows.map((row) => watchedTypePrices[row._idx]?.[dimensionValueField] ?? null);
  }, [isSelectDimension, dimensionRows, watchedTypePrices, dimensionValueField]);
};

const useEnsureDimensionRows = ({
  selectedDimension,
  hasDefaultRow,
  prepend,
}: {
  selectedDimension: TPriceDimension;
  hasDefaultRow: boolean;
  prepend: UseFieldArrayPrepend<TProductForm, "typePrices">;
}): void => {
  useEffect(() => {
    // 国籍 / 分野 / ティア keep a pinned 全て… default row on top; その他 has none (C3).
    if (dimensionHasDefaultRow(selectedDimension) && !hasDefaultRow) {
      prepend(defaultTypePriceForm(selectedDimension));
    }
  }, [selectedDimension, hasDefaultRow, prepend]);
};

export const useProductTypePriceSection = ({
  control,
  productName,
  isTierDiscountTarget,
  fields,
  watchedTypePrices,
  prepend,
  remove,
  rangeCountCell,
  nationalityOptions,
  industryOptions,
}: TUseProductTypePriceSectionParams): {
  availableDimensions: TPriceDimension[];
  selectedDimension: TPriceDimension;
  setSelectedDimension: Dispatch<SetStateAction<TPriceDimension>>;
  dimensionRows: TProductTypePriceRow[];
  columns: MRT_ColumnDef<TProductTypePriceRow>[];
} => {
  const availableDimensions = useMemo(
    () => getTypeDimensions(productName, isTierDiscountTarget),
    [productName, isTierDiscountTarget],
  );

  const [selectedDimension, setSelectedDimension] = useState<TPriceDimension>(availableDimensions[0]);

  useEffect(() => {
    if (!availableDimensions.includes(selectedDimension)) {
      setSelectedDimension(availableDimensions[0]);
    }
  }, [availableDimensions, selectedDimension]);

  const dimensionRows = useMemo(
    () => fields.filter((row) => row.dimension === selectedDimension),
    [fields, selectedDimension],
  );

  const { hasDefaultRow } = useDimensionRowStats(dimensionRows);
  const selectedDimensionValues = useSelectedDimensionValues(selectedDimension, dimensionRows, watchedTypePrices);

  useEnsureDimensionRows({
    selectedDimension,
    hasDefaultRow,
    prepend,
  });

  const columns = useMemo(
    () =>
      buildColumnsByDimension({
        selectedDimension,
        selectedDimensionValues,
        control,
        remove,
        rangeCountCell,
        nationalityOptions,
        industryOptions,
      }),
    [selectedDimension, selectedDimensionValues, control, remove, rangeCountCell, nationalityOptions, industryOptions],
  );

  return { availableDimensions, selectedDimension, setSelectedDimension, dimensionRows, columns };
};
